import type { SerializedBoardDto, TileDto } from '../api/puzzles';
import type { GameSnapshot, LifeStatus, MovePlayed, SealedLife, StagedPlacement } from './store';

const PREFIX = 'sd-local-life:';

/** Bumped when the saved shape changes. v1 saves stay playable but unclaimable. */
export const SAVE_VERSION = 2;

/** The slice of guest game state that survives a refresh. */
export interface SavedLocalLife {
  version?: number;
  /** Puzzle date — entries from previous days get pruned. */
  date: string;
  board: SerializedBoardDto;
  rack: TileDto[];
  bag: TileDto[];
  staged: StagedPlacement[];
  cumulativeScore: number;
  movesUsed: number;
  moveHistory?: MovePlayed[];
  previousLives?: SealedLife[];
  lifeNumber: number;
  status: LifeStatus;
  dayWon: boolean;
}

export interface ClaimLifeInput {
  life_number: number;
  status: LifeStatus;
  moves: Array<{ placements: StagedPlacement[]; score: number }>;
}

export function saveLocalLife(puzzleId: string, date: string, s: GameSnapshot): void {
  try {
    const saved: SavedLocalLife = {
      version: SAVE_VERSION,
      date,
      board: s.board,
      rack: s.rack,
      bag: s.bag,
      staged: s.staged,
      cumulativeScore: s.cumulativeScore,
      movesUsed: s.movesUsed,
      moveHistory: s.moveHistory,
      previousLives: s.previousLives,
      lifeNumber: s.lifeNumber,
      status: s.status ?? 'IN_PROGRESS',
      dayWon: s.dayWon,
    };
    localStorage.setItem(PREFIX + puzzleId, JSON.stringify(saved));
    // Prune entries from previous days (each difficulty keeps its own entry).
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key?.startsWith(PREFIX) || key === PREFIX + puzzleId) continue;
      try {
        const other = JSON.parse(localStorage.getItem(key) ?? '{}') as { date?: string };
        if (other.date !== date) localStorage.removeItem(key);
      } catch {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Storage full / disabled — guest persistence is best-effort.
  }
}

export function loadLocalLife(puzzleId: string): SavedLocalLife | null {
  try {
    const raw = localStorage.getItem(PREFIX + puzzleId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedLocalLife;
    if (!Array.isArray(parsed.rack) || typeof parsed.lifeNumber !== 'number' || !parsed.status) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export interface GuestDayState {
  state: 'not_started' | 'in_progress' | 'won' | 'lost';
  lives_used: number;
  lives_remaining: number;
  best_score: number;
}

/** Derive the landing-card state for a guest from their saved local life. */
export function guestDayState(saved: SavedLocalLife | null): GuestDayState {
  if (!saved) {
    return { state: 'not_started', lives_used: 0, lives_remaining: 3, best_score: 0 };
  }
  const lifeEnded = saved.status !== 'IN_PROGRESS';
  const livesUsed = lifeEnded ? saved.lifeNumber : saved.lifeNumber - 1;
  const state = saved.dayWon
    ? 'won'
    : saved.status === 'LOST' && saved.lifeNumber >= 3
      ? 'lost'
      : 'in_progress';
  // The guest's best for the day, not just whatever the current life has
  // scored so far — mirrors claimGuestRun's max-across-lives on the server.
  const bestScore = Math.max(
    saved.cumulativeScore,
    ...(saved.previousLives ?? []).map((l) => l.final_score),
  );
  return {
    state,
    lives_used: livesUsed,
    lives_remaining: Math.max(0, 3 - livesUsed),
    best_score: bestScore,
  };
}

/**
 * Cheap, storage-only check for whether any guest life is saved at all —
 * no puzzle id, no parsing, just "is there a sd-local-life: key". Lets
 * callers skip network work entirely when there's plainly nothing to claim,
 * without needing to know which puzzle to ask about.
 */
export function hasAnyLocalSave(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      if (localStorage.key(i)?.startsWith(PREFIX)) return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function clearLocalLife(puzzleId: string): void {
  try {
    localStorage.removeItem(PREFIX + puzzleId);
  } catch {
    // ignore
  }
}

/**
 * Flatten a saved day into the claim payload. Returns null when there is
 * nothing to claim — a v1 save (no recorded placements) or a life with no
 * moves played yet.
 */
export function claimLivesFrom(saved: SavedLocalLife | null): ClaimLifeInput[] | null {
  if (!saved || saved.version !== SAVE_VERSION) return null;

  const lives: ClaimLifeInput[] = (saved.previousLives ?? []).map((l) => ({
    life_number: l.life_number,
    status: l.status,
    moves: l.moves.map((m) => ({ placements: m.placements, score: m.score })),
  }));

  const current = saved.moveHistory ?? [];
  if (current.length > 0) {
    lives.push({
      life_number: saved.lifeNumber,
      status: saved.status,
      moves: current.map((m) => ({ placements: m.placements, score: m.score })),
    });
  }

  return lives.length > 0 ? lives : null;
}
